import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import '../styles/BillingSetup.css';
import { Checkbox, DateInput, NumberInput, Text, formatDate, formatValue } from './FieldComponents';
import { CreateEntry } from './CreateEntry';
import CreateFlatFee from './CreateFlatFee';
import Modal from './Modal';
import MoveFolderPicker from './MoveFolderPicker';
import Alert from './Alert';
import { ChevronDown, ExternalLink, FileText, Filter, Link, Plus, Unlink } from 'lucide-react';
import { dispatchAppAlert } from '../utils/appAlerts';
import {
    buildBillingEntryEditData,
    buildBillingEntryUpdatePayload,
    readBillingEntryMutationResponse,
} from './billingEntryMutation';

const invoiceStatusOptions = [
    { value: 'draft', label: 'Draft' },
    { value: 'sent', label: 'Sent' },
    { value: 'unpaid', label: 'Unpaid' },
    { value: 'partially_paid', label: 'Partially Paid' },
    { value: 'paid', label: 'Paid' },
    { value: 'overdue', label: 'Overdue' },
    { value: 'void', label: 'Void' },
    { value: 'cancelled', label: 'Cancelled' }
];

const ENTRY_PAGE_SIZE = 100;

const getLocalDateInputValue = (date = new Date()) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const normalizeEntryDate = (value) => {
    const match = String(value || '').match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : '';
};

export const formatBillingEntryDate = (value) => {
    const normalized = normalizeEntryDate(value);
    if (!normalized) return formatDate(value)[3];

    const [year, month, day] = normalized.split('-').map(Number);
    return `${month}/${day}/${year}`;
};

export const getBillingEntryDateRange = (entries = []) => {
    const dates = Array.from(new Set(
        (Array.isArray(entries) ? entries : [])
            .map(entry => normalizeEntryDate(
                entry?.date
                || entry?.entry_date
                || entry?.snapshot_date
                || entry?.source_date_value
            ))
            .filter(Boolean)
    )).sort();

    if (dates.length === 0) return '';

    const firstDate = formatBillingEntryDate(dates[0]);
    const lastDate = formatBillingEntryDate(dates[dates.length - 1]);
    return firstDate === lastDate ? firstDate : `${firstDate}–${lastDate}`;
};

const toFiniteNumber = (value, fallback = 0) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
};

const toOptionalFiniteNumber = (value) => {
    if (
        value === null
        || value === undefined
        || typeof value === 'boolean'
        || (typeof value === 'string' && value.trim() === '')
    ) return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
};

const roundCurrency = (value) => {
    const number = toFiniteNumber(value);
    const sign = number < 0 ? -1 : 1;
    return sign * (Math.round((Math.abs(number) + Number.EPSILON) * 100) / 100);
};

const getBillingEntryQuantity = (entry = {}) => {
    if (entry.final_quantity !== null && entry.final_quantity !== undefined && entry.final_quantity !== '') {
        return entry.final_quantity;
    }
    return entry.raw_quantity ?? entry.quantity ?? entry.hours;
};

export const getBillingEntryTotal = (entry = {}) => {
    const storedTotal = toOptionalFiniteNumber(entry.final_total)
        ?? toOptionalFiniteNumber(entry.amount);
    if (storedTotal !== null) return roundCurrency(storedTotal);

    return roundCurrency(
        toFiniteNumber(entry.rate)
            * toFiniteNumber(getBillingEntryQuantity(entry), 0)
    );
};

export const getInvoiceDisplayTotal = ({
    invoiceData = {},
    selectedEntryIds = [],
    availableEntriesById = new Map(),
    selectedCaseExpenseIds = [],
    availableCaseExpensesById = new Map(),
    isLoadingEntries = false,
    loadError = '',
} = {}) => {
    const storedTotal = toFiniteNumber(invoiceData?.invoice?.total);
    const preservesSavedSnapshotTotal = Boolean(invoiceData?.invoice?.id)
        && invoiceData?.replaceSelectedEntries !== true
        && invoiceData?.replaceSelectedCaseExpenses !== true;
    const hasEverySelectedEntry = selectedEntryIds.every(entryId => availableEntriesById.has(entryId));
    const hasEverySelectedCaseExpense = selectedCaseExpenseIds.every(
        sourceId => availableCaseExpensesById.has(sourceId)
    );
    if (
        isLoadingEntries
        || loadError
        || preservesSavedSnapshotTotal
        || !hasEverySelectedEntry
        || !hasEverySelectedCaseExpense
    ) {
        return storedTotal;
    }
    const billingEntryTotal = selectedEntryIds.reduce((total, entryId) => (
        total + getBillingEntryTotal(availableEntriesById.get(entryId))
    ), 0);
    return billingEntryTotal + selectedCaseExpenseIds.reduce((total, sourceId) => (
        total + getBillingEntryTotal(availableCaseExpensesById.get(sourceId))
    ), 0);
};

export const billingEntryIsNewInvoiceEligible = (entry = {}) => {
    const explicitlyNonbillable = entry.billable !== null
        && entry.billable !== undefined
        && entry.billable !== ''
        && Number(entry.billable) === 0;
    const isDraft = entry.draft !== null
        && entry.draft !== undefined
        && entry.draft !== ''
        && Number(entry.draft) !== 0;
    return !explicitlyNonbillable
        && !isDraft
        && Number(entry.deleted || 0) === 0
        && Number(entry.status || 0) !== 2;
};

export const matchesBillingEntryFilters = (entry = {}, filters = {}) => {
    const entryDate = normalizeEntryDate(entry.date || entry.entry_date || entry.created_at);
    const dateFrom = normalizeEntryDate(filters.dateFrom);
    const dateTo = normalizeEntryDate(filters.dateTo);
    if (dateFrom && (!entryDate || entryDate < dateFrom)) return false;
    if (dateTo && (!entryDate || entryDate > dateTo)) return false;

    const query = String(filters.query || '').trim().toLowerCase();
    if (!query) return true;

    return [
        entry.date,
        entry.description,
        entry.payee_name,
        entry.payee,
        entry.source_invoice_number,
        entry.item_type_label,
        entry.item_type_value,
        entry.memo,
        entry.more_info,
        entry.flat_fee_code,
        entry.flat_fee_code_2,
        entry.flat_fee_code2,
        entry.unit,
        entry.status,
        filters.userName
    ].some(value => String(value || '').toLowerCase().includes(query));
};

export const getProgressiveEntries = (entries = [], limit = ENTRY_PAGE_SIZE) => (
    entries.slice(0, Math.max(ENTRY_PAGE_SIZE, Number(limit) || ENTRY_PAGE_SIZE))
);

const normalizeInvoiceStatus = (status) => {
    const raw = String(status || 'draft').trim().toLowerCase().replace(/[\s-]+/g, '_');
    if (raw === 'drafting' || raw === 'editing') return 'draft';
    if (raw === 'final') return 'sent';
    if (raw === 'partial' || raw === 'partial_paid' || raw === 'partiallypaid') return 'partially_paid';
    if (raw === 'canceled') return 'cancelled';
    if (raw === 'voided') return 'void';
    return invoiceStatusOptions.some(option => option.value === raw) ? raw : 'draft';
};

export const invoiceCanBeEdited = (invoice = {}) => (
    normalizeInvoiceStatus(invoice?.status) !== 'paid'
);

const normalizeBillingEntryId = (value) => {
    const id = Number(value);
    return Number.isInteger(id) && id > 0 ? id : null;
};

const getBillingEntryKind = (entry = {}) => {
    const kind = String(entry.entry_kind || entry.item_type || 'time').trim().toLowerCase();
    if (kind === 'flatfee' || kind === 'flat') return 'flat_fee';
    return kind;
};

const explicitFalse = (value) => (
    value === false
    || value === 0
    || ['0', 'false', 'no'].includes(String(value ?? '').trim().toLowerCase())
);

export const getBillingEntryEffectiveUserId = (entry = {}, billingRateUsers = null) => {
    const directCandidates = [
        entry.effective_user_id,
        entry.user_id,
        entry.billing_rate_user_id,
        entry.rate_user_id,
        entry.timekeeper_user_id,
        entry.user?.id,
        entry.billing_rate?.user_id,
    ];
    for (const candidate of directCandidates) {
        const userId = normalizeBillingEntryId(candidate);
        if (userId) return userId;
    }

    const billingRateId = normalizeBillingEntryId(entry.billing_rate_id);
    if (!billingRateId || !billingRateUsers) return null;
    if (billingRateUsers instanceof Map) {
        return normalizeBillingEntryId(
            billingRateUsers.get(billingRateId) ?? billingRateUsers.get(String(billingRateId))
        );
    }
    if (Array.isArray(billingRateUsers)) {
        const rate = billingRateUsers.find(candidate => (
            normalizeBillingEntryId(candidate?.id) === billingRateId
        ));
        return normalizeBillingEntryId(rate?.user_id ?? rate?.effective_user_id);
    }
    return null;
};

export const getBillingTimeEntryAuthoritativeTotal = (entry = {}) => {
    if (entry.final_total !== null && entry.final_total !== undefined && entry.final_total !== '') {
        const finalTotal = toOptionalFiniteNumber(entry.final_total);
        return finalTotal === null ? null : roundCurrency(finalTotal);
    }
    const rate = toOptionalFiniteNumber(entry.rate);
    const quantity = toOptionalFiniteNumber(getBillingEntryQuantity(entry));
    if (rate === null || quantity === null) return null;
    return roundCurrency(rate * quantity);
};

export const getBillingTimeEntryInvoiceIssue = (entry = {}, billingRateUsers = null) => {
    if (getBillingEntryKind(entry) !== 'time') return '';
    if (explicitFalse(entry.invoice_eligible)) {
        return String(
            entry.invoice_ineligible_reason
            || entry.invoice_eligibility_reason
            || 'This time entry is not eligible for invoicing.'
        );
    }
    if (!getBillingEntryEffectiveUserId(entry, billingRateUsers)) {
        return 'Assign a team member before adding this time entry to an invoice.';
    }
    const rate = toOptionalFiniteNumber(entry.rate);
    const quantity = toOptionalFiniteNumber(getBillingEntryQuantity(entry));
    if (rate === null || quantity === null) {
        return 'Enter a valid rate and quantity before adding this time entry to an invoice.';
    }
    const authoritativeTotal = getBillingTimeEntryAuthoritativeTotal(entry);
    if (authoritativeTotal === null || authoritativeTotal <= 0) {
        return 'The time entry total must be greater than $0 before it can be added to an invoice.';
    }
    return '';
};

export const billingEntryIsEligibleForInvoiceSelection = (entry = {}, billingRateUsers = null) => {
    if (!billingEntryIsNewInvoiceEligible(entry)) return false;
    if (getBillingEntryKind(entry) !== 'time') return true;
    return getBillingTimeEntryInvoiceIssue(entry, billingRateUsers) === '';
};

const normalizeCaseExpenseId = (value) => {
    const id = Number(value);
    return Number.isInteger(id) && id > 0 ? id : null;
};

export const normalizeCaseExpenseSource = (source = {}) => {
    const sourceId = normalizeCaseExpenseId(source.source_field_update_id);
    const amount = toOptionalFiniteNumber(source.amount);
    return {
        ...source,
        source_field_update_id: sourceId,
        date: source.snapshot_date || source.source_date_value || '',
        description: source.description || source.memo || source.more_info || 'Case expense',
        payee_name: source.payee_name || '',
        invoice_number: source.source_invoice_number || '',
        rate: amount,
        raw_quantity: 1,
        final_quantity: 1,
        final_total: amount,
        unit: 'each',
        entry_kind: 'expense',
        source_type: 'case_expense',
    };
};

export const mergeCaseExpenseSources = (sources = [], linkedSnapshots = []) => {
    const bySourceId = new Map();
    (Array.isArray(sources) ? sources : []).forEach(source => {
        const normalized = normalizeCaseExpenseSource(source);
        if (normalized.source_field_update_id) {
            bySourceId.set(normalized.source_field_update_id, normalized);
        }
    });
    // Saved snapshots are authoritative for an existing invoice and remain
    // visible even when the original Expenses item was edited or removed.
    (Array.isArray(linkedSnapshots) ? linkedSnapshots : []).forEach(snapshot => {
        const normalized = normalizeCaseExpenseSource({
            ...snapshot,
            available: true,
            unavailable_reason: null,
        });
        if (normalized.source_field_update_id) {
            bySourceId.set(normalized.source_field_update_id, normalized);
        }
    });
    return Array.from(bySourceId.values());
};

const getBillingEntryIds = (entries = []) => Array.from(new Set(
    entries
        .map(entry => normalizeBillingEntryId(entry?.id))
        .filter(Boolean)
));

const invoiceLinkFields = [
    'invoice_id', 'time_invoice_id', 'time_invoice', 'time_invoices_id', 'billing_invoice_id',
    'invoice_group_id', 'invoiced', 'invoiced_at', 'time_invoice_item_id', 'time_invoice_item',
    'invoice_number', 'time_invoice_number'
];

const getBillingEntryInvoiceIds = (entry = {}) => [
    entry.time_invoice_id,
    entry.invoice_id,
    entry.time_invoices_id,
    entry.billing_invoice_id
].map(normalizeBillingEntryId).filter(Boolean);

export const billingEntryHasInvoiceLink = (entry = {}) => invoiceLinkFields.some(field => {
    const value = entry[field];
    return value !== undefined
        && value !== null
        && value !== ''
        && value !== false
        && value !== 0
        && value !== '0';
});

export const billingEntryCanBeEditedFromInvoice = (entry = {}) => !billingEntryHasInvoiceLink(entry);

export const billingEntryIsSelectableForInvoice = (entry, invoiceId = null, billingRateUsers = null) => {
    const linkedInvoiceIds = getBillingEntryInvoiceIds(entry);
    if (!billingEntryHasInvoiceLink(entry)) {
        return billingEntryIsEligibleForInvoiceSelection(entry, billingRateUsers);
    }
    if (!invoiceId || linkedInvoiceIds.length === 0) return false;
    return linkedInvoiceIds.every(linkedInvoiceId => linkedInvoiceId === invoiceId);
};

export const getInvoiceStatusLabel = (status) => {
    const normalized = normalizeInvoiceStatus(status);
    return invoiceStatusOptions.find(option => option.value === normalized)?.label || 'Draft';
};

export const buildInvoiceBillingEntryPayload = (entry = {}, kind = 'time', originalEntry = null) => {
    if (entry.id) return buildBillingEntryUpdatePayload(entry, originalEntry);

    const payload = { ...entry };
    const quantity = Number(payload.hours ?? payload.quantity ?? payload.raw_quantity ?? (kind === 'flat_fee' ? 1 : 0));
    const normalizedQuantity = Number.isFinite(quantity) ? quantity : (kind === 'flat_fee' ? 1 : 0);
    payload.item_type = kind;
    payload.entry_kind = kind;
    payload.raw_quantity = normalizedQuantity;
    payload.quantity = normalizedQuantity;
    payload.hours = normalizedQuantity;
    if (kind === 'flat_fee') payload.user_id = null;
    return payload;
};

export const CreatePayment = ({
    payment,
    setPayment
}) => {
    return (
        <div className='modal-content-wrapper'>
            <div className='form-group alt mid'>
                <label className='subtext'>Reference #</label>
                <Text
                    type='text'
                    placeholder='Reference #'
                    value={payment?.reference}
                    onChange={(val) => setPayment({ ...payment, reference: val })}
                />
            </div>
            <div className='form-group alt mid'>
                <label className='subtext'>Date</label>
                <DateInput
                    value={payment?.date}
                    onChange={(val) => setPayment({ ...payment, date: val })}
                />
            </div>
            <div className='form-group alt mid'>
                <label className='subtext'>Total*</label>
                <NumberInput
                    type='currency'
                    value={payment?.total}
                    onChange={(val) => setPayment({ ...payment, total: val })}
                />
            </div>
            <div className='form-group alt mid'>
                <label className='subtext'>Method*</label>
                <Text
                    type='text'
                    placeholder='Method*'
                    value={payment?.method}
                    onChange={(val) => setPayment({ ...payment, method: val })}
                />
            </div>
        </div>
    );
};

export const CreateInvoice = ({
    caseData,
    timeEntries,
    rateEntries = [],
    onClose,
    saveInvoice,
    invoiceData,
    setInvoiceData,
    isSavingInvoice = false,
    markInvoiceDataPersisted = null
}) => {
    const [addDescription, setAddDescription] = useState(Boolean(
        invoiceData?.invoice?.invoice_description || invoiceData?.description
    ));
    const [showEntryFilters, setShowEntryFilters] = useState(false);
    const [availableTimeEntries, setAvailableTimeEntries] = useState([]);
    const [availableFlatFeeEntries, setAvailableFlatFeeEntries] = useState([]);
    const [availableExpenseEntries, setAvailableExpenseEntries] = useState([]);
    const [availableCaseExpenses, setAvailableCaseExpenses] = useState([]);
    const [expandedTime, setExpandedTime] = useState(false);
    const [expandedFlat, setExpandedFlat] = useState(false);
    const [expandedExpenses, setExpandedExpenses] = useState(false);
    const [users, setUsers] = useState([]);
    const [flatFeeOptions, setFlatFeeOptions] = useState([]);
    const [flatFeeOptions2, setFlatFeeOptions2] = useState([]);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [originalEntryData, setOriginalEntryData] = useState(null);
    const [entryMessage, setEntryMessage] = useState(null);
    const [isSavingEntry, setIsSavingEntry] = useState(false);
    const [isLoadingEntries, setIsLoadingEntries] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [operationMessage, setOperationMessage] = useState(null);
    const [entrySearch, setEntrySearch] = useState('');
    const [entryDateFrom, setEntryDateFrom] = useState('');
    const [entryDateTo, setEntryDateTo] = useState('');
    const [timeEntryLimit, setTimeEntryLimit] = useState(ENTRY_PAGE_SIZE);
    const [expenseEntryLimit, setExpenseEntryLimit] = useState(ENTRY_PAGE_SIZE);
    const [caseExpenseLimit, setCaseExpenseLimit] = useState(ENTRY_PAGE_SIZE);
    const [flatFeeEntryLimit, setFlatFeeEntryLimit] = useState(ENTRY_PAGE_SIZE);
    const [generatingPdf, setGeneratingPdf] = useState(false);
    const editorBusy = isSavingInvoice || generatingPdf;
    const invoiceReadOnly = !invoiceCanBeEdited(invoiceData?.invoice);
    const invoiceEditDisabled = editorBusy || invoiceReadOnly;
    const entrySaveInFlightRef = useRef(false);
    const entryLoadRequestRef = useRef({ generation: 0, controller: null });
    const lastSelectionAnchorRef = useRef(null);
    const lastCaseExpenseSelectionAnchorRef = useRef(null);
    const [showInvoiceFilePicker, setShowInvoiceFilePicker] = useState(false);
    const [createKind, setCreateKind] = useState('time'); // 'time' | 'expense' | 'flat_fee'
    const [createEntryData, setCreateEntryData] = useState({
        date: invoiceData?.invoice?.invoice_date || getLocalDateInputValue(),
        rate: 0,
        hours: 1,
        description: '',
        user_id: null,
        case_id: caseData?.case?.id
    });

    const getDefaultEntryData = (kind = 'time') => ({
        date: invoiceData?.invoice?.invoice_date || getLocalDateInputValue(),
        rate: 0,
        hours: 1,
        raw_quantity: 1,
        quantity: 1,
        description: '',
        flat_fee_code: '',
        flat_fee_code_2: '',
        unit: kind === 'expense' ? 'each' : '',
        user_id: kind === 'time' ? undefined : null,
        draft: false,
        case_id: caseData?.case?.id,
        billable: true,
        chargeable: true,
        item_type: kind,
        entry_kind: kind
    });

    const closeCreateEntryModal = () => {
        if (entrySaveInFlightRef.current || editorBusy) return;
        setShowCreateModal(false);
        setOriginalEntryData(null);
        setCreateEntryData(getDefaultEntryData(createKind));
    };

    const openNewBillingEntry = (kind) => {
        if (entrySaveInFlightRef.current || invoiceEditDisabled) return;
        setCreateKind(kind);
        setOriginalEntryData(null);
        setEntryMessage(null);
        setCreateEntryData(getDefaultEntryData(kind));
        setShowCreateModal(true);
    };

    const invoiceCaseId = caseData?.case?.id || caseData?.case?.case_id || caseData?.case_id;
    const invoiceStatus = normalizeInvoiceStatus(invoiceData?.invoice?.status);
    const invoicePaidAmount = Math.max(0, toFiniteNumber(invoiceData?.invoice?.total_paid));
    const statusIsPaymentDerived = invoicePaidAmount > 0
        && !['void', 'cancelled'].includes(invoiceStatus);
    const linkedInvoiceFileId = invoiceData?.invoice?.invoice_file_id || invoiceData?.invoice?.linked_file?.id || null;
    const linkedInvoiceFileName = invoiceData?.invoice?.invoice_file_name || invoiceData?.invoice?.linked_file?.file_name || '';
    const selectedEntryIds = useMemo(() => Array.from(new Set(
        (Array.isArray(invoiceData?.selectedEntries) ? invoiceData.selectedEntries : [])
            .map(normalizeBillingEntryId)
            .filter(Boolean)
    )), [invoiceData?.selectedEntries]);
    const selectedEntryIdSet = useMemo(() => new Set(selectedEntryIds), [selectedEntryIds]);
    const selectedCaseExpenseIds = useMemo(() => Array.from(new Set(
        (Array.isArray(invoiceData?.selectedCaseExpenseIds) ? invoiceData.selectedCaseExpenseIds : [])
            .map(normalizeCaseExpenseId)
            .filter(Boolean)
    )), [invoiceData?.selectedCaseExpenseIds]);
    const selectedCaseExpenseIdSet = useMemo(
        () => new Set(selectedCaseExpenseIds),
        [selectedCaseExpenseIds]
    );
    const selectedTimeEntries = useMemo(() => availableTimeEntries.filter(
        entry => selectedEntryIdSet.has(normalizeBillingEntryId(entry.id))
    ), [availableTimeEntries, selectedEntryIdSet]);
    const selectedBillingExpenseEntries = useMemo(() => availableExpenseEntries.filter(
        entry => selectedEntryIdSet.has(normalizeBillingEntryId(entry.id))
    ), [availableExpenseEntries, selectedEntryIdSet]);
    const selectedCaseExpenses = useMemo(() => availableCaseExpenses.filter(
        expense => selectedCaseExpenseIdSet.has(normalizeCaseExpenseId(expense.source_field_update_id))
    ), [availableCaseExpenses, selectedCaseExpenseIdSet]);
    const selectedExpenseEntries = useMemo(() => (
        [...selectedCaseExpenses, ...selectedBillingExpenseEntries]
    ), [selectedBillingExpenseEntries, selectedCaseExpenses]);
    const selectedFlatFeeEntries = useMemo(() => availableFlatFeeEntries.filter(
        entry => selectedEntryIdSet.has(normalizeBillingEntryId(entry.id))
    ), [availableFlatFeeEntries, selectedEntryIdSet]);
    const timeSelectionDateRange = useMemo(
        () => getBillingEntryDateRange(selectedTimeEntries),
        [selectedTimeEntries]
    );
    const expenseSelectionDateRange = useMemo(
        () => getBillingEntryDateRange(selectedExpenseEntries),
        [selectedExpenseEntries]
    );
    const flatFeeSelectionDateRange = useMemo(
        () => getBillingEntryDateRange(selectedFlatFeeEntries),
        [selectedFlatFeeEntries]
    );
    const userNameById = useMemo(() => new Map(
        (Array.isArray(users) ? users : []).map(user => [Number(user.id), user.name || ''])
    ), [users]);
    const billingRateUserById = useMemo(() => new Map(
        (Array.isArray(rateEntries) ? rateEntries : [])
            .map(rate => [normalizeBillingEntryId(rate?.id), normalizeBillingEntryId(rate?.user_id)])
            .filter(([rateId, userId]) => rateId && userId)
    ), [rateEntries]);
    const filterOptions = useMemo(() => ({
        query: entrySearch,
        dateFrom: entryDateFrom,
        dateTo: entryDateTo
    }), [entryDateFrom, entryDateTo, entrySearch]);
    const filterEntries = useCallback((entries) => entries.filter(entry => matchesBillingEntryFilters(entry, {
        ...filterOptions,
        userName: userNameById.get(getBillingEntryEffectiveUserId(entry, billingRateUserById))
            || entry.user_name
            || ''
    })), [billingRateUserById, filterOptions, userNameById]);
    const filteredTimeEntries = useMemo(
        () => filterEntries(availableTimeEntries),
        [availableTimeEntries, filterEntries]
    );
    const filteredExpenseEntries = useMemo(
        () => filterEntries(availableExpenseEntries),
        [availableExpenseEntries, filterEntries]
    );
    const filteredCaseExpenses = useMemo(
        () => filterEntries(availableCaseExpenses),
        [availableCaseExpenses, filterEntries]
    );
    const filteredFlatFeeEntries = useMemo(
        () => filterEntries(availableFlatFeeEntries),
        [availableFlatFeeEntries, filterEntries]
    );
    const visibleTimeEntries = useMemo(
        () => getProgressiveEntries(filteredTimeEntries, timeEntryLimit),
        [filteredTimeEntries, timeEntryLimit]
    );
    const visibleExpenseEntries = useMemo(
        () => getProgressiveEntries(filteredExpenseEntries, expenseEntryLimit),
        [expenseEntryLimit, filteredExpenseEntries]
    );
    const visibleCaseExpenses = useMemo(
        () => getProgressiveEntries(filteredCaseExpenses, caseExpenseLimit),
        [caseExpenseLimit, filteredCaseExpenses]
    );
    const visibleFlatFeeEntries = useMemo(
        () => getProgressiveEntries(filteredFlatFeeEntries, flatFeeEntryLimit),
        [filteredFlatFeeEntries, flatFeeEntryLimit]
    );
    const timeEntryIds = useMemo(() => getBillingEntryIds(visibleTimeEntries), [visibleTimeEntries]);
    const flatFeeEntryIds = useMemo(() => getBillingEntryIds(visibleFlatFeeEntries), [visibleFlatFeeEntries]);
    const expenseEntryIds = useMemo(() => getBillingEntryIds(visibleExpenseEntries), [visibleExpenseEntries]);
    const caseExpenseIds = useMemo(() => visibleCaseExpenses
        .map(expense => normalizeCaseExpenseId(expense.source_field_update_id))
        .filter(Boolean), [visibleCaseExpenses]);
    const availableEntriesById = useMemo(() => {
        const entriesById = new Map();
        [...availableTimeEntries, ...availableExpenseEntries, ...availableFlatFeeEntries].forEach(entry => {
            const entryId = normalizeBillingEntryId(entry.id);
            if (entryId) entriesById.set(entryId, entry);
        });
        return entriesById;
    }, [availableExpenseEntries, availableFlatFeeEntries, availableTimeEntries]);
    const availableCaseExpensesById = useMemo(() => new Map(
        availableCaseExpenses
            .map(expense => [normalizeCaseExpenseId(expense.source_field_update_id), expense])
            .filter(([sourceId]) => sourceId)
    ), [availableCaseExpenses]);
    const missingSelectedEntryCount = useMemo(() => selectedEntryIds.filter(
        entryId => !availableEntriesById.has(entryId)
    ).length + selectedCaseExpenseIds.filter(
        sourceId => !availableCaseExpensesById.has(sourceId)
    ).length, [availableCaseExpensesById, availableEntriesById, selectedCaseExpenseIds, selectedEntryIds]);
    const selectionLoadError = loadError || (
        !isLoadingEntries && missingSelectedEntryCount > 0
            ? `${missingSelectedEntryCount.toLocaleString()} selected ${missingSelectedEntryCount === 1 ? 'entry could' : 'entries could'} not be loaded. Retry before changing this invoice's selections.`
            : ''
    );
    const selectionNeedsRecalculation = !invoiceData?.invoice?.id
        || invoiceData?.replaceSelectedEntries === true
        || invoiceData?.replaceSelectedCaseExpenses === true;
    const selectionSaveBlocked = selectionNeedsRecalculation && (
        isLoadingEntries
        || Boolean(selectionLoadError)
        || missingSelectedEntryCount > 0
    );
    const liveSelectedTotal = useMemo(() => getInvoiceDisplayTotal({
        invoiceData,
        selectedEntryIds,
        availableEntriesById,
        selectedCaseExpenseIds,
        availableCaseExpensesById,
        isLoadingEntries,
        loadError: selectionLoadError,
    }), [availableCaseExpensesById, availableEntriesById, invoiceData, isLoadingEntries, selectionLoadError, selectedCaseExpenseIds, selectedEntryIds]);
    const visibleEntryIdSet = useMemo(() => new Set(getBillingEntryIds([
        ...visibleTimeEntries,
        ...visibleExpenseEntries,
        ...visibleFlatFeeEntries
    ])), [visibleExpenseEntries, visibleFlatFeeEntries, visibleTimeEntries]);
    const hiddenSelectedEntryCount = useMemo(() => selectedEntryIds.filter(
        entryId => availableEntriesById.has(entryId) && !visibleEntryIdSet.has(entryId)
    ).length + selectedCaseExpenseIds.filter(
        sourceId => availableCaseExpensesById.has(sourceId) && !caseExpenseIds.includes(sourceId)
    ).length, [availableCaseExpensesById, availableEntriesById, caseExpenseIds, selectedCaseExpenseIds, selectedEntryIds, visibleEntryIdSet]);
    const activeEntryFilterCount = [entrySearch.trim(), entryDateFrom, entryDateTo]
        .filter(Boolean).length;
    const hasActiveEntryFilters = activeEntryFilterCount > 0;
    useEffect(() => {
        setTimeEntryLimit(ENTRY_PAGE_SIZE);
        setExpenseEntryLimit(ENTRY_PAGE_SIZE);
        setCaseExpenseLimit(ENTRY_PAGE_SIZE);
        setFlatFeeEntryLimit(ENTRY_PAGE_SIZE);
        lastSelectionAnchorRef.current = null;
        lastCaseExpenseSelectionAnchorRef.current = null;
    }, [entryDateFrom, entryDateTo, entrySearch]);

    const updateEntrySelection = useCallback((entryIds, shouldSelect) => {
        if (invoiceEditDisabled) return;
        const normalizedIds = entryIds.map(normalizeBillingEntryId).filter(Boolean);
        setInvoiceData(prev => {
            const nextSelection = new Set(
                (Array.isArray(prev?.selectedEntries) ? prev.selectedEntries : [])
                    .map(normalizeBillingEntryId)
                    .filter(Boolean)
            );

            normalizedIds.forEach(id => {
                if (shouldSelect) nextSelection.add(id);
                else nextSelection.delete(id);
            });

            return { ...prev, selectedEntries: Array.from(nextSelection), replaceSelectedEntries: true };
        });
    }, [invoiceEditDisabled, setInvoiceData]);

    const toggleEntrySelection = useCallback((entryId, orderedEntryIds, event) => {
        const normalizedId = normalizeBillingEntryId(entryId);
        if (!normalizedId) return;

        const normalizedOrder = orderedEntryIds.map(normalizeBillingEntryId).filter(Boolean);
        const anchorId = lastSelectionAnchorRef.current;
        const anchorIndex = normalizedOrder.indexOf(anchorId);
        const currentIndex = normalizedOrder.indexOf(normalizedId);
        const shouldSelect = !selectedEntryIdSet.has(normalizedId);

        if (event?.shiftKey && anchorIndex !== -1 && currentIndex !== -1) {
            const rangeStart = Math.min(anchorIndex, currentIndex);
            const rangeEnd = Math.max(anchorIndex, currentIndex);
            updateEntrySelection(normalizedOrder.slice(rangeStart, rangeEnd + 1), shouldSelect);
        } else {
            updateEntrySelection([normalizedId], shouldSelect);
        }

        lastSelectionAnchorRef.current = normalizedId;
    }, [selectedEntryIdSet, updateEntrySelection]);

    const updateCaseExpenseSelection = useCallback((sourceIds, shouldSelect) => {
        if (invoiceEditDisabled) return;
        const normalizedIds = sourceIds.map(normalizeCaseExpenseId).filter(Boolean);
        setInvoiceData(prev => {
            const nextSelection = new Set(
                (Array.isArray(prev?.selectedCaseExpenseIds) ? prev.selectedCaseExpenseIds : [])
                    .map(normalizeCaseExpenseId)
                    .filter(Boolean)
            );
            normalizedIds.forEach(sourceId => {
                if (shouldSelect) nextSelection.add(sourceId);
                else nextSelection.delete(sourceId);
            });
            return {
                ...prev,
                selectedCaseExpenseIds: Array.from(nextSelection),
                replaceSelectedCaseExpenses: true,
            };
        });
    }, [invoiceEditDisabled, setInvoiceData]);

    const toggleCaseExpenseSelection = useCallback((sourceId, orderedSourceIds, event) => {
        const normalizedId = normalizeCaseExpenseId(sourceId);
        if (!normalizedId) return;
        const source = availableCaseExpensesById.get(normalizedId);
        if (!selectedCaseExpenseIdSet.has(normalizedId) && source?.available === false) return;

        const normalizedOrder = orderedSourceIds.map(normalizeCaseExpenseId).filter(Boolean);
        const anchorId = lastCaseExpenseSelectionAnchorRef.current;
        const anchorIndex = normalizedOrder.indexOf(anchorId);
        const currentIndex = normalizedOrder.indexOf(normalizedId);
        const shouldSelect = !selectedCaseExpenseIdSet.has(normalizedId);
        if (event?.shiftKey && anchorIndex !== -1 && currentIndex !== -1) {
            const rangeStart = Math.min(anchorIndex, currentIndex);
            const rangeEnd = Math.max(anchorIndex, currentIndex);
            const selectableRange = normalizedOrder.slice(rangeStart, rangeEnd + 1).filter(candidateId => (
                shouldSelect
                    ? availableCaseExpensesById.get(candidateId)?.available !== false
                    : true
            ));
            updateCaseExpenseSelection(selectableRange, shouldSelect);
        } else {
            updateCaseExpenseSelection([normalizedId], shouldSelect);
        }
        lastCaseExpenseSelectionAnchorRef.current = normalizedId;
    }, [availableCaseExpensesById, selectedCaseExpenseIdSet, updateCaseExpenseSelection]);

    const setInvoiceStatus = (status) => {
        if (invoiceEditDisabled) return;
        setInvoiceData(prev => ({
            ...prev,
            invoice: {
                ...prev.invoice,
                status: normalizeInvoiceStatus(status)
            }
        }));
    };

    const setLinkedInvoiceFile = (file) => {
        if (invoiceEditDisabled) return;
        const fileId = file?.id || file?.file_id;
        if (!fileId) {
            setOperationMessage({
                type: 'error',
                message: 'That file does not have a file record yet, so it cannot be linked to the invoice.'
            });
            return;
        }

        setOperationMessage(null);
        setInvoiceData(prev => ({
            ...prev,
            replaceInvoiceFile: true,
            invoice: {
                ...prev.invoice,
                invoice_file: String(fileId),
                invoice_file_id: Number(fileId),
                invoice_file_name: file.name || file.file_name || `File ${fileId}`,
                invoice_file_path: file.file_path || file.key || file.path || null,
                invoice_file_url: file.file_url || file.url || null,
                linked_file: {
                    id: Number(fileId),
                    file_name: file.name || file.file_name || `File ${fileId}`,
                    file_path: file.file_path || file.key || file.path || null,
                    file_url: file.file_url || file.url || null
                }
            }
        }));
        setShowInvoiceFilePicker(false);
    };

    const clearLinkedInvoiceFile = () => {
        if (invoiceEditDisabled) return;
        setInvoiceData(prev => ({
            ...prev,
            replaceInvoiceFile: true,
            invoice: {
                ...prev.invoice,
                invoice_file: null,
                invoice_file_id: null,
                invoice_file_name: '',
                invoice_file_path: null,
                invoice_file_url: null,
                linked_file: null
            }
        }));
    };

    const fetchInvoiceFolderContents = useCallback(async (currentFolder = '{{Name}}', page = 1, pageSize = 100) => {
        if (!invoiceCaseId) return null;

        try {
            const params = new URLSearchParams({
                folder_fetch: 'true',
                case_id: String(invoiceCaseId),
                current_folder: currentFolder || '{{Name}}',
                page: String(page),
                page_size: String(pageSize),
                t: String(Date.now())
            });

            const response = await fetch(`https://api.casedb.co/documents.php?${params}`, { credentials: 'include' });
            const data = await response.json();

            if (!data.success) {
                console.error('Failed to fetch invoice file picker contents:', data);
                return null;
            }

            return {
                items: data.items || [],
                folders: data.folders || [],
                files: data.files || [],
                metaData: data.metaData || [],
                pagination: data.pagination || {},
                currentFolder: data.current_folder || '',
                folderPrefix: data.folder_prefix || ''
            };
        } catch (err) {
            console.error('Failed to fetch invoice file picker contents:', err);
            return null;
        }
    }, [invoiceCaseId]);

    const editFlatFeeEntry = (entry) => {
        if (entrySaveInFlightRef.current || invoiceEditDisabled) return;
        setCreateKind('flat_fee');
        setOriginalEntryData({ ...entry });
        setEntryMessage(null);
        setCreateEntryData(buildBillingEntryEditData(entry, caseData?.case?.id));
        setShowCreateModal(true);
    };

    const fetchUsers = async () => {
        try {
            const response = await fetch('https://api.casedb.co/user.php?billing_rate=1', {
                credentials: 'include'
            });
            const data = await response.json();
            if (data.success) {
                setUsers(data.users);
            }
        } catch (err) {
            console.error('Failed to fetch users:', err);
        }
    };

    const setAvailableBillingEntries = (entries = []) => {
        setAvailableTimeEntries(entries.filter(entry => (
            entry.entry_kind === 'time'
            || entry.item_type === 'time'
            || (!entry.entry_kind && entry.item_type === undefined)
        )));
        setAvailableExpenseEntries(entries.filter(entry => (
            entry.entry_kind === 'expense' || entry.item_type === 'expense'
        )));
        setAvailableFlatFeeEntries(entries.filter(entry => (
            entry.entry_kind === 'flat_fee' || entry.item_type === 'flat_fee'
        )));
    };

    const setAvailableCaseExpenseSources = (sources = [], linkedSnapshots = []) => {
        setAvailableCaseExpenses(mergeCaseExpenseSources(sources, linkedSnapshots));
    };

    const beginEntryLoadRequest = () => {
        const previousRequest = entryLoadRequestRef.current;
        previousRequest.controller?.abort();

        const request = {
            generation: previousRequest.generation + 1,
            controller: new AbortController(),
        };
        entryLoadRequestRef.current = request;
        setIsLoadingEntries(true);
        setLoadError('');
        return request;
    };

    const entryLoadRequestIsCurrent = (request) => (
        entryLoadRequestRef.current.generation === request.generation
        && !request.controller.signal.aborted
    );

    const fetchAvailableTimeEntries = async () => {
        const request = beginEntryLoadRequest();
        try {
            const editingInvoiceId = normalizeBillingEntryId(invoiceData?.invoice?.id);
            const url = `https://api.casedb.co/billing_entries.php?case_id=${invoiceCaseId}&status=pending&include_case_expenses=1`;
                
            const response = await fetch(url, {
                credentials: 'include',
                signal: request.controller.signal,
            });
            const data = await response.json().catch(() => ({}));
            if (!entryLoadRequestIsCurrent(request)) return null;
            if (!response.ok || !data.success) {
                throw new Error(data.message || `Billing entries could not be loaded (${response.status}).`);
            }

            const all = (data.billing_entries || []).filter(entry => entry.status !== '2' && entry.status !== 2);
            const filteredEntries = all.filter(entry => billingEntryIsSelectableForInvoice(
                entry,
                editingInvoiceId,
                billingRateUserById
            ));
            setAvailableBillingEntries(filteredEntries);
            setAvailableCaseExpenseSources(data.case_expenses || []);
            return filteredEntries;
        } catch (err) {
            if (err.name === 'AbortError' || !entryLoadRequestIsCurrent(request)) return null;
            console.error('Failed to fetch time entries:', err);
            setLoadError(err.message || 'Billing entries could not be loaded.');
            return null;
        } finally {
            if (entryLoadRequestIsCurrent(request)) setIsLoadingEntries(false);
        }
    };

    const fetchInvoiceItems = async (invoiceId) => {
        const request = beginEntryLoadRequest();
        try {
            const currentInvoiceId = normalizeBillingEntryId(invoiceId);
            const linkedEntryIdSet = new Set(selectedEntryIds);
            const entriesRes = await fetch(
                `https://api.casedb.co/billing_entries.php?case_id=${invoiceCaseId}&include_case_expenses=1&current_invoice_id=${currentInvoiceId}`,
                {
                    credentials: 'include',
                    signal: request.controller.signal,
                }
            );
            const entriesData = await entriesRes.json().catch(() => ({}));
            if (!entryLoadRequestIsCurrent(request)) return null;
            if (!entriesRes.ok || !entriesData.success) {
                throw new Error(entriesData.message || `Billing entries could not be loaded (${entriesRes.status}).`);
            }

            const allEntries = entriesData.billing_entries || [];
            const selectableEntries = allEntries.filter(entry => {
                const entryId = normalizeBillingEntryId(entry.id);
                if (entryId && linkedEntryIdSet.has(entryId)) return true;
                const isPending = entry.status !== '2' && entry.status !== 2;
                return isPending && billingEntryIsSelectableForInvoice(
                    entry,
                    currentInvoiceId,
                    billingRateUserById
                );
            });
            setAvailableBillingEntries(selectableEntries);
            setAvailableCaseExpenseSources(
                entriesData.case_expenses || [],
                invoiceData?.linkedCaseExpenses || []
            );
            return selectableEntries;
        } catch (err) {
            if (err.name === 'AbortError' || !entryLoadRequestIsCurrent(request)) return null;
            console.error('Failed to fetch invoice items:', err);
            setLoadError(err.message || 'Invoice entries could not be loaded.');
            return null;
        } finally {
            if (entryLoadRequestIsCurrent(request)) setIsLoadingEntries(false);
        }
    };

    const postBillingEntry = async (payload) => {
        if (entrySaveInFlightRef.current) return false;

        entrySaveInFlightRef.current = true;
        setIsSavingEntry(true);
        setEntryMessage(null);
        try {
            const isUpdate = !!payload.id;
            const response = await fetch('https://api.casedb.co/billing_entries.php', {
                method: isUpdate ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(payload)
            });
            const data = await readBillingEntryMutationResponse(
                response,
                `The billing entry could not be ${isUpdate ? 'updated' : 'created'}`
            );
            const editingInvoiceId = normalizeBillingEntryId(invoiceData?.invoice?.id);
            const refreshedEntries = editingInvoiceId
                ? await fetchInvoiceItems(editingInvoiceId)
                : await fetchAvailableTimeEntries();
            if (!Array.isArray(refreshedEntries)) {
                return {
                    success: true,
                    data,
                    entries: [],
                    refreshFailed: true,
                    message: 'The billing entry was saved, but the invoice entries could not be refreshed. Retry before saving the invoice.',
                };
            }
            const noChanges = isUpdate && (data.no_changes === true || data.changed === false || Number(data.affected_rows) === 0);
            setEntryMessage({
                type: noChanges ? 'info' : 'success',
                message: data.message || (noChanges ? 'No billing entry changes were needed.' : `Billing entry ${isUpdate ? 'updated' : 'created'} successfully.`),
            });
            return { success: true, data, entries: refreshedEntries };
        } catch (err) {
            console.error('Error saving billing entry:', err);
            setEntryMessage({
                type: 'error',
                message: err.message || 'The billing entry could not be saved. Please try again.',
            });
            return { success: false, data: null, entries: [] };
        } finally {
            entrySaveInFlightRef.current = false;
            setIsSavingEntry(false);
        }
    };

    const saveBillingEntry = async () => {
        if (entrySaveInFlightRef.current || invoiceEditDisabled) return;

        const isUpdate = Boolean(createEntryData?.id);
        const payload = buildInvoiceBillingEntryPayload(createEntryData, createKind, originalEntryData);
        const saveResult = await postBillingEntry(payload);
        if (!saveResult.success) return;

        setShowCreateModal(false);
        setOriginalEntryData(null);
        setCreateEntryData(getDefaultEntryData(createKind));
        if (saveResult.refreshFailed) {
            setEntryMessage({ type: 'error', message: saveResult.message });
        }
        if (isUpdate) return;

        setInvoiceData((prev) => {
            const existing = new Set(
                (Array.isArray(prev?.selectedEntries) ? prev.selectedEntries : [])
                    .map(normalizeBillingEntryId)
                    .filter(Boolean)
            );
            const createdEntryId = normalizeBillingEntryId(saveResult.data?.billing_entry_id);
            if (!createdEntryId) return prev;
            existing.add(createdEntryId);
            return { ...prev, selectedEntries: Array.from(existing), replaceSelectedEntries: true };
        });
    };

    const fetchFlatFeeCodes = async () => {
        const response = await fetch('https://api.casedb.co/billing_codes.php');
        const data = await response.json();
        if (data.success) {
            setFlatFeeOptions(data.flat_fee_code || []);
            setFlatFeeOptions2(data.flat_fee_code_2 || []);
        } else {
            console.error('Failed to fetch flat fee codes:', data);
        }
    };

    useEffect(() => {
        // Always fetch all available entries for the case
        if (invoiceCaseId) {
            setAvailableCaseExpenses([]);
            fetchUsers();
            fetchFlatFeeCodes();
            if (invoiceData?.invoice?.id) {
                fetchInvoiceItems(invoiceData.invoice.id);
            } else {
                fetchAvailableTimeEntries();
            }
        }
        return () => {
            const activeRequest = entryLoadRequestRef.current;
            activeRequest.controller?.abort();
            entryLoadRequestRef.current = {
                generation: activeRequest.generation + 1,
                controller: null,
            };
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoiceCaseId, invoiceData?.invoice?.id]);

    const saveCurrentInvoice = async () => {
        if (invoiceEditDisabled || selectionSaveBlocked) return;

        setOperationMessage(null);
        const result = await saveInvoice({ invoiceData });
        if (!result?.success) {
            setOperationMessage({
                type: 'error',
                message: result?.message || 'The invoice could not be saved. Please try again.'
            });
        }
    };

    const generatePdf = async () => {
        if (invoiceEditDisabled || selectionSaveBlocked) return;

        const caseId = caseData?.case?.id;
        if (!caseId && !invoiceData?.invoice?.id) {
            setOperationMessage({ type: 'error', message: 'No invoice or case ID is available.' });
            return;
        }

        setOperationMessage(null);
        setGeneratingPdf(true);
        try {
            const saved = await saveInvoice({ keepOpen: true, invoiceData });
            if (!saved?.success) {
                throw new Error(saved?.message || 'The invoice must be saved before its document can be generated.');
            }
            const invoiceId = Number(saved?.invoice_id || saved?.invoice?.id || invoiceData?.invoice?.id || 0);
            if (!invoiceId) throw new Error('The saved invoice did not return an invoice ID.');

            const formData = new FormData();
            formData.append('invoice_id', invoiceId);
            const savedRevision = saved?.invoice?.revision || invoiceData?.invoice?.revision;
            if (savedRevision) formData.append('expected_revision', savedRevision);

            const res = await fetch('https://api.casedb.co/generate_invoice.php', {
                method: 'POST',
                body: formData,
                credentials: 'include'
            });
            
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data.success) {
                throw new Error(data.message || `The invoice document could not be generated (${res.status}).`);
            }

            const generatedFile = data.file || {};
            const persistGeneratedInvoice = (prev) => ({
                    ...prev,
                    invoice: { 
                        ...prev.invoice, 
                        id: invoiceId,
                        invoice_file: String(data.file_id),
                        invoice_file_id: Number(data.file_id),
                        invoice_file_name: data.file_name || generatedFile.file_name || `File ${data.file_id}`,
                        invoice_file_path: data.file_path || generatedFile.file_path || null,
                        invoice_file_url: generatedFile.file_url || null,
                        linked_file: {
                            ...generatedFile,
                            id: Number(data.file_id),
                            file_id: Number(data.file_id)
                        },
                        revision: data.invoice_revision || savedRevision || prev.invoice.revision,
                        last_generated: new Date().toISOString()
                    }
                });
            if (typeof markInvoiceDataPersisted === 'function') {
                markInvoiceDataPersisted(persistGeneratedInvoice);
            } else {
                setInvoiceData(persistGeneratedInvoice);
            }

            dispatchAppAlert({
                eventType: 'invoice_generated',
                type: 'success',
                title: 'Invoice saved',
                message: `Saved in Case Documents → ${data.folder_name || 'Word Processing'} and linked to this invoice.`,
                actionHref: `/file/${Number(data.file_id)}`,
                actionLabel: 'Open file',
                duration: 8000,
                dismissOnClick: false,
                showClose: true
            });
        } catch (err) {
            console.error('Error generating invoice', err);
            setOperationMessage({
                type: 'error',
                message: err.message || 'The invoice document could not be generated.'
            });
        } finally {
            setGeneratingPdf(false);
        }
    };

    const name = caseData?.case?.case_name || "";
    const spaceCount = name.split(" ").length - 1;
    const filteredExpenseCount = filteredExpenseEntries.length + filteredCaseExpenses.length;
    const availableExpenseCount = availableExpenseEntries.length + availableCaseExpenses.length;

    return (
        <>
        <Modal
            onClose={() => {
                if (!editorBusy) onClose();
            }}
            title={
                <div className='invoice-header'>
                    <div className='invoice-header-data' role='group' aria-label='Invoice details'>
                        <div className='invoice-header-info'>
                            Invoice
                            <span className='tag caps bright'>{getInvoiceStatusLabel(invoiceStatus)}</span>
                        </div>
                        <div className='invoice-case-data'>
                            <span className='contact-initials'>
                                {caseData?.case?.case_name?.charAt(0)}{spaceCount === 1 ? caseData?.case?.case_name.split(" ")[1].charAt(0) : null}
                            </span>
                            {caseData?.case?.case_name}
                        </div>
                        <div className='invoice-description-section invoice-header-description-section'>
                            {addDescription ? (
                                <div className='form-group nm full invoice-description-field'>
                                    <label className='subtext'>Invoice Description</label>
                                    <Text
                                        type='textarea'
                                        placeholder='Add an invoice description...'
                                        value={invoiceData?.invoice?.invoice_description || ''}
                                        disable={invoiceEditDisabled}
                                        onChange={(val) => setInvoiceData(prev => ({
                                            ...prev,
                                            invoice: { ...prev.invoice, invoice_description: val }
                                        }))}
                                    />
                                </div>
                            ) : (
                                <button
                                    type='button'
                                    className='invoice-add-description-button'
                                    onClick={() => setAddDescription(true)}
                                    disabled={invoiceEditDisabled}
                                >
                                    <Plus size={15} />
                                    Add invoice description
                                </button>
                            )}
                        </div>
                    </div>
                    <div className='create-invoice'>
                        <div className='invoice-data first-col form-group alt nm full'>
                            <label className='subtext' style={{ width: 'auto', minWidth: '75px' }}>Invoice Date</label>
                            <DateInput
                                value={invoiceData?.invoice?.invoice_date?.includes(" ") ? invoiceData?.invoice?.invoice_date.split(" ")[0] : invoiceData?.invoice?.invoice_date}
                                disable={invoiceEditDisabled}
                                onChange={(val) => setInvoiceData(prev => ({ ...prev, invoice: { ...prev.invoice, invoice_date: val } }))}
                            />
                        </div>
                        <div className='invoice-data first-col form-group alt nm full'>
                            <label className='subtext' style={{ width: 'auto', minWidth: '75px' }}>Due Date</label>
                            <DateInput
                                value={invoiceData?.invoice?.due_date?.includes(" ") ? invoiceData?.invoice?.due_date.split(" ")[0] : invoiceData?.invoice?.due_date}
                                disable={invoiceEditDisabled}
                                onChange={(val) => setInvoiceData(prev => ({ ...prev, invoice: { ...prev.invoice, due_date: val } }))}
                            />
                        </div>
                        <div className='invoice-data two-col form-group alt nm full'>
                            <label className='subtext' style={{ width: 'auto', minWidth: '75px' }}>Date Range</label>
                            <div className='date-range-container'>
                                <DateInput
                                    value={invoiceData?.invoice?.start_date?.includes(" ") ? invoiceData?.invoice?.start_date.split(" ")[0] : invoiceData?.invoice?.start_date}
                                    disable={invoiceEditDisabled}
                                    onChange={(val) => setInvoiceData(prev => ({ ...prev, invoice: { ...prev.invoice, start_date: val } }))}
                                />
                                <span className='subtext'>to</span>
                                <DateInput
                                    value={invoiceData?.invoice?.end_date?.includes(" ") ? invoiceData?.invoice?.end_date.split(" ")[0] : invoiceData?.invoice?.end_date}
                                    disable={invoiceEditDisabled}
                                    onChange={(val) => setInvoiceData(prev => ({ ...prev, invoice: { ...prev.invoice, end_date: val } }))}
                                />
                            </div>
                        </div>
                        <div className='invoice-data third-col first-row' style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <div className='subtext invoice-data'><span>Invoice #</span><span>{invoiceData?.invoice?.invoice_number}</span></div>
                            <div
                                className='subtext invoice-data invoice-status-row'
                                title='Paid and partially paid statuses are updated from invoice transactions.'
                            >
                                <span>Status</span>
                                <select
                                    className='default-select invoice-status-select'
                                    value={invoiceStatus}
                                    aria-label='Invoice status'
                                    disabled={invoiceEditDisabled}
                                    onChange={(event) => setInvoiceStatus(event.target.value)}
                                >
                                    {invoiceStatusOptions.map(option => (
                                        <option
                                            key={option.value}
                                            value={option.value}
                                            disabled={
                                                (invoicePaidAmount <= 0 && ['paid', 'partially_paid'].includes(option.value))
                                                || (statusIsPaymentDerived
                                                    && !['paid', 'partially_paid', 'void', 'cancelled'].includes(option.value))
                                            }
                                        >
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className='subtext invoice-data invoice-file-link-row'>
                                <span>Invoice File</span>
                                <div className='invoice-linked-file-controls'>
                                    {linkedInvoiceFileId ? (
                                        <>
                                            <button
                                                type='button'
                                                className='invoice-linked-file-name'
                                                title='Open linked invoice file'
                                                disabled={editorBusy}
                                                onClick={() => window.open(`/file/${linkedInvoiceFileId}`, '_blank')}
                                            >
                                                <FileText size={14} />
                                                <span>{linkedInvoiceFileName || `File ${linkedInvoiceFileId}`}</span>
                                                <ExternalLink size={13} />
                                            </button>
                                            <button
                                                type='button'
                                                className='action alt invoice-file-icon-action'
                                                title='Remove linked invoice file'
                                                aria-label='Remove linked invoice file'
                                                disabled={invoiceEditDisabled}
                                                onClick={clearLinkedInvoiceFile}
                                            >
                                                <Unlink size={14} />
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            type='button'
                                            className='action-micro invoice-link-file-button'
                                            title='Link an invoice file'
                                            aria-label='Link an invoice file'
                                            disabled={invoiceEditDisabled}
                                            onClick={() => setShowInvoiceFilePicker(true)}
                                        >
                                            <Link size={14} />
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className='subtext invoice-data'><span>Total</span><span aria-live='polite'>{formatValue(liveSelectedTotal, "currency")}</span></div>
                        </div>
                    </div>
                </div>
            }
            wider={true}
            footer={
                <div className='modal-footer-actions'>
                    <button type='button' className='action alt' onClick={onClose} disabled={editorBusy}>Cancel</button>
                    {(invoiceData?.invoice?.invoice_number || caseData?.case?.id) && <button
                        type='button'
                        className={'action tertiary' + (generatingPdf || selectionSaveBlocked ? ' disabled' : '')}
                        style={{ backgroundColor: 'var(--background-color)' }}
                        onClick={generatePdf}
                        disabled={invoiceEditDisabled || selectionSaveBlocked}
                        aria-busy={generatingPdf}
                    >
                        {generatingPdf ? 'Generating Invoice...' : 'Generate Invoice'}
                    </button>}
                    <button type='button' className='action' onClick={saveCurrentInvoice} disabled={invoiceEditDisabled || selectionSaveBlocked} aria-busy={isSavingInvoice}>
                        {isSavingInvoice ? 'Saving…' : 'Save'}
                    </button>
                </div>
            }
        >
            <div className='modal-content-wrapper invoice-editor-content' aria-busy={editorBusy || isLoadingEntries}>
                {invoiceReadOnly && (
                    <div className='invoice-editor-message' role='status'>
                        This invoice is fully paid and read-only. Remove or adjust its payment allocation before changing invoice details or entries.
                    </div>
                )}
                {operationMessage && (
                    <div className={`invoice-editor-message ${operationMessage.type}`} role={operationMessage.type === 'error' ? 'alert' : 'status'}>
                        {operationMessage.message}
                    </div>
                )}
                <div className='invoice-entry-filter-toolbar' role='group' aria-label='Billing entry controls'>
                    <button
                        type='button'
                        className={`invoice-add-description-button invoice-filter-toggle${showEntryFilters ? ' active' : ''}`}
                        aria-expanded={showEntryFilters}
                        aria-controls='invoice-entry-filters'
                        onClick={() => setShowEntryFilters(prev => !prev)}
                        disabled={editorBusy}
                    >
                        <Filter size={15} aria-hidden='true' />
                        Filter entries{activeEntryFilterCount > 0 ? ` (${activeEntryFilterCount})` : ''}
                    </button>
                </div>
                {showEntryFilters && (
                    <div id='invoice-entry-filters' className='invoice-entry-filters' role='search' aria-label='Filter billing entries'>
                        <label className='invoice-entry-search-filter'>
                            <span>Search entries</span>
                            <input
                                type='search'
                                value={entrySearch}
                                disabled={editorBusy}
                                placeholder='Description, person, code, or status'
                                onChange={(event) => setEntrySearch(event.target.value)}
                            />
                        </label>
                        <label>
                            <span>From</span>
                            <input
                                type='date'
                                value={entryDateFrom}
                                disabled={editorBusy}
                                onChange={(event) => setEntryDateFrom(event.target.value)}
                            />
                        </label>
                        <label>
                            <span>To</span>
                            <input
                                type='date'
                                value={entryDateTo}
                                disabled={editorBusy}
                                onChange={(event) => setEntryDateTo(event.target.value)}
                            />
                        </label>
                        {hasActiveEntryFilters && (
                            <button
                                type='button'
                                className='action alt invoice-filter-clear'
                                disabled={editorBusy}
                                onClick={() => {
                                    setEntrySearch('');
                                    setEntryDateFrom('');
                                    setEntryDateTo('');
                                }}
                            >
                                Clear filters
                            </button>
                        )}
                        <div className='invoice-filter-status' role='status' aria-live='polite'>
                            {isLoadingEntries
                                ? 'Loading billing entries…'
                                : hiddenSelectedEntryCount > 0
                                    ? `${hiddenSelectedEntryCount.toLocaleString()} selected ${hiddenSelectedEntryCount === 1 ? 'entry is' : 'entries are'} outside the current view. The selection will be preserved.`
                                    : 'Shift-click checkboxes to select or clear a visible range.'}
                        </div>
                    </div>
                )}
                {selectionLoadError && (
                    <div className='invoice-editor-message error' role='alert'>
                        <span>{selectionLoadError}</span>
                        <button
                            type='button'
                            className='action alt'
                            disabled={editorBusy || isLoadingEntries}
                            onClick={() => {
                                if (invoiceData?.invoice?.id) fetchInvoiceItems(invoiceData.invoice.id);
                                else fetchAvailableTimeEntries();
                            }}
                        >
                            Retry
                        </button>
                    </div>
                )}
                {/* Time Entries */}
                <section className='time-entries-section' aria-labelledby='invoice-time-entries-heading'>
                    <div className='invoice-entry-section-header'>
                        <h3 id='invoice-time-entries-heading'>
                            <button
                                type='button'
                                className='invoice-section-toggle'
                                aria-expanded={expandedTime}
                                aria-controls='invoice-time-entries-content'
                                disabled={editorBusy}
                                onClick={() => setExpandedTime(prev => !prev)}
                            >
                                <span className='invoice-section-toggle-copy'>
                                    <span className='invoice-section-title'>Time Entries</span>
                                    <span className='invoice-section-summary'>
                                        <span className='invoice-section-summary-item'>{selectedTimeEntries.length.toLocaleString()} selected</span>
                                        {timeSelectionDateRange && (
                                            <span className='invoice-section-summary-item invoice-section-summary-detail'>
                                                <span className='invoice-section-summary-separator' aria-hidden='true'>•</span>
                                                <span>{timeSelectionDateRange}</span>
                                            </span>
                                        )}
                                        <span className='invoice-section-summary-item invoice-section-summary-detail'>
                                            <span className='invoice-section-summary-separator' aria-hidden='true'>•</span>
                                            <span>{filteredTimeEntries.length.toLocaleString()}{hasActiveEntryFilters ? ` of ${availableTimeEntries.length.toLocaleString()}` : ''} available</span>
                                        </span>
                                    </span>
                                </span>
                                <ChevronDown className={expandedTime ? 'expanded' : ''} size={16} aria-hidden='true' />
                            </button>
                        </h3>
                        <button
                            type='button'
                            className='add-time-entry'
                            title='Add time entry'
                            aria-label='Add time entry'
                            disabled={invoiceEditDisabled}
                            onClick={() => openNewBillingEntry('time')}
                        >
                            <Plus size={15} aria-hidden='true' />
                        </button>
                    </div>
                    {expandedTime && (
                        <div id='invoice-time-entries-content'>
                            {filteredTimeEntries.length === 0 ? (
                                <div className='empty-state'>{hasActiveEntryFilters ? 'No time entries match the current filters.' : 'No available time entries for this invoice.'}</div>
                            ) : (
                                <>
                                    <div className='invoice-entry-table-scroll' role='region' aria-label='Time entries' tabIndex={0}>
                                        <table className='payment-details-table table-full default invoice-entry-table invoice-time-entry-table'>
                                            <thead>
                                                <tr className='default'>
                                                    <th scope='col'>Date</th>
                                                    <th scope='col' className='invoice-entry-description'>Description</th>
                                                    <th scope='col'>Team Member</th>
                                                    <th scope='col'>Rate</th>
                                                    <th scope='col'>Hours</th>
                                                    <th scope='col'>Total</th>
                                                    <th scope='col'>Status</th>
                                                    <th scope='col'>Select</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {visibleTimeEntries.map((entry) => {
                                                    const entryId = normalizeBillingEntryId(entry.id);
                                                    const effectiveUserId = getBillingEntryEffectiveUserId(
                                                        entry,
                                                        billingRateUserById
                                                    );
                                                    const invoiceIssue = getBillingTimeEntryInvoiceIssue(
                                                        entry,
                                                        billingRateUserById
                                                    );
                                                    const issueId = invoiceIssue ? `time-entry-invoice-issue-${entryId}` : null;
                                                    return (
                                                    <tr key={entry.id} className='default'>
                                                        <td>{formatBillingEntryDate(entry.date)}</td>
                                                        <td className='invoice-entry-description'>{entry.description}</td>
                                                        <td>{userNameById.get(effectiveUserId) || entry.user_name || 'Unassigned'}</td>
                                                        <td>{formatValue(toFiniteNumber(entry.rate), 'currency')}</td>
                                                        <td>{toFiniteNumber(entry.final_quantity ?? entry.raw_quantity).toFixed(2)}</td>
                                                        <td>{formatValue(getBillingEntryTotal(entry), 'currency')}</td>
                                                        <td>
                                                            <span id={issueId} className='tag' title={invoiceIssue || ''}>
                                                                {invoiceIssue ? 'Needs correction' : entry.status || 'Saved'}
                                                            </span>
                                                        </td>
                                                        <td className='invoice-entry-select-cell'>
                                                            <Checkbox
                                                                value={selectedEntryIdSet.has(entryId)}
                                                                disabled={invoiceEditDisabled}
                                                                ariaLabel={`Include time entry ${entry.description || entry.id} on invoice`}
                                                                ariaDescribedBy={issueId}
                                                                onChange={(event) => toggleEntrySelection(entry.id, timeEntryIds, event)}
                                                            />
                                                        </td>
                                                    </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                    {visibleTimeEntries.length < filteredTimeEntries.length && (
                                        <button type='button' className='invoice-load-more' disabled={editorBusy} onClick={() => setTimeEntryLimit(limit => limit + ENTRY_PAGE_SIZE)}>
                                            Load {Math.min(ENTRY_PAGE_SIZE, filteredTimeEntries.length - visibleTimeEntries.length).toLocaleString()} more time entries
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </section>
                {/* Expenses */}
                <section className='time-entries-section' aria-labelledby='invoice-expense-entries-heading'>
                    <div className='invoice-entry-section-header'>
                        <h3 id='invoice-expense-entries-heading'>
                            <button
                                type='button'
                                className='invoice-section-toggle'
                                aria-expanded={expandedExpenses}
                                aria-controls='invoice-expense-entries-content'
                                disabled={editorBusy}
                                onClick={() => setExpandedExpenses(prev => !prev)}
                            >
                                <span className='invoice-section-toggle-copy'>
                                    <span className='invoice-section-title'>Expenses</span>
                                    <span className='invoice-section-summary'>
                                        <span className='invoice-section-summary-item'>{selectedExpenseEntries.length.toLocaleString()} selected</span>
                                        {expenseSelectionDateRange && (
                                            <span className='invoice-section-summary-item invoice-section-summary-detail'>
                                                <span className='invoice-section-summary-separator' aria-hidden='true'>•</span>
                                                <span>{expenseSelectionDateRange}</span>
                                            </span>
                                        )}
                                        <span className='invoice-section-summary-item invoice-section-summary-detail'>
                                            <span className='invoice-section-summary-separator' aria-hidden='true'>•</span>
                                            <span>{filteredExpenseCount.toLocaleString()}{hasActiveEntryFilters ? ` of ${availableExpenseCount.toLocaleString()}` : ''} available</span>
                                        </span>
                                    </span>
                                </span>
                                <ChevronDown className={expandedExpenses ? 'expanded' : ''} size={16} aria-hidden='true' />
                            </button>
                        </h3>
                        <button
                            type='button'
                            className='add-time-entry'
                            title='Add custom expense'
                            aria-label='Add custom expense'
                            disabled={invoiceEditDisabled}
                            onClick={() => openNewBillingEntry('expense')}
                        >
                            <Plus size={15} aria-hidden='true' />
                        </button>
                    </div>
                    {expandedExpenses && (
                        <div id='invoice-expense-entries-content'>
                            {filteredExpenseCount === 0 ? (
                                <div className='empty-state'>{hasActiveEntryFilters ? 'No expenses match the current filters.' : 'No available expenses for this invoice.'}</div>
                            ) : (
                                <>
                                    {filteredCaseExpenses.length > 0 && (
                                        <div className='invoice-entry-source-group'>
                                            <div className='invoice-entry-source-label'>Case expenses</div>
                                            <div className='invoice-entry-table-scroll' role='region' aria-label='Case expenses' tabIndex={0}>
                                                <table className='payment-details-table table-full default invoice-entry-table invoice-case-expense-table'>
                                                    <thead>
                                                        <tr className='default'>
                                                            <th scope='col'>Date</th>
                                                            <th scope='col' className='invoice-entry-description'>Description</th>
                                                            <th scope='col'>Payee</th>
                                                            <th scope='col'>Invoice #</th>
                                                            <th scope='col'>Type</th>
                                                            <th scope='col'>Amount</th>
                                                            <th scope='col'>Status</th>
                                                            <th scope='col'>Select</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {visibleCaseExpenses.map((expense) => {
                                                            const sourceId = normalizeCaseExpenseId(expense.source_field_update_id);
                                                            const selected = selectedCaseExpenseIdSet.has(sourceId);
                                                            const unavailable = expense.available === false && !selected;
                                                            const dateDisplay = formatBillingEntryDate(expense.date) || expense.source_date_value || '';
                                                            return (
                                                                <tr key={`case-expense-${sourceId}`} className={`default ${unavailable ? 'invoice-entry-unavailable' : ''}`}>
                                                                    <td>{dateDisplay}</td>
                                                                    <td className='invoice-entry-description'>{expense.description}</td>
                                                                    <td>{expense.payee_name || ''}</td>
                                                                    <td>{expense.invoice_number || ''}</td>
                                                                    <td>{expense.item_type_label || expense.item_type_value || ''}</td>
                                                                    <td>{expense.amount === null ? '' : formatValue(toFiniteNumber(expense.amount), 'currency')}</td>
                                                                    <td>
                                                                        <span className='tag' title={expense.unavailable_reason || ''}>
                                                                            {selected
                                                                                ? 'On invoice'
                                                                                : unavailable
                                                                                    ? 'Unavailable'
                                                                                    : 'Available'}
                                                                        </span>
                                                                    </td>
                                                                    <td className='invoice-entry-select-cell'>
                                                                        <Checkbox
                                                                            value={selected}
                                                                            disabled={invoiceEditDisabled || unavailable}
                                                                            ariaLabel={`Include case expense ${expense.description || sourceId} on invoice`}
                                                                            ariaDescribedBy={unavailable ? `case-expense-reason-${sourceId}` : null}
                                                                            onChange={(event) => toggleCaseExpenseSelection(sourceId, caseExpenseIds, event)}
                                                                        />
                                                                        {unavailable && (
                                                                            <span id={`case-expense-reason-${sourceId}`} className='sr-only'>
                                                                                {expense.unavailable_reason}
                                                                            </span>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                            {visibleCaseExpenses.length < filteredCaseExpenses.length && (
                                                <button type='button' className='invoice-load-more' disabled={editorBusy} onClick={() => setCaseExpenseLimit(limit => limit + ENTRY_PAGE_SIZE)}>
                                                    Load {Math.min(ENTRY_PAGE_SIZE, filteredCaseExpenses.length - visibleCaseExpenses.length).toLocaleString()} more case expenses
                                                </button>
                                            )}
                                        </div>
                                    )}
                                    {filteredExpenseEntries.length > 0 && (
                                        <div className='invoice-entry-source-group'>
                                            <div className='invoice-entry-source-label'>Billing expenses</div>
                                            <div className='invoice-entry-table-scroll' role='region' aria-label='Billing expense entries' tabIndex={0}>
                                                <table className='payment-details-table table-full default invoice-entry-table invoice-expense-entry-table'>
                                                    <thead>
                                                        <tr className='default'>
                                                            <th scope='col'>Date</th>
                                                            <th scope='col' className='invoice-entry-description'>Description</th>
                                                            <th scope='col'>Payee</th>
                                                            <th scope='col'>Quantity</th>
                                                            <th scope='col'>Amount</th>
                                                            <th scope='col'>Unit</th>
                                                            <th scope='col'>Total</th>
                                                            <th scope='col'>Status</th>
                                                            <th scope='col'>Select</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {visibleExpenseEntries.map((entry) => (
                                                            <tr key={entry.id} className='default'>
                                                                <td>{formatBillingEntryDate(entry.date)}</td>
                                                                <td className='invoice-entry-description'>{entry.description}</td>
                                                                <td>{entry.payee_name || entry.payee || ''}</td>
                                                                <td>{toFiniteNumber(entry.final_quantity ?? entry.raw_quantity).toFixed(2)}</td>
                                                                <td>{formatValue(toFiniteNumber(entry.rate), 'currency')}</td>
                                                                <td>{entry.unit}</td>
                                                                <td>{formatValue(getBillingEntryTotal(entry), 'currency')}</td>
                                                                <td><span className='tag'>{entry.status || 'Saved'}</span></td>
                                                                <td className='invoice-entry-select-cell'>
                                                                    <Checkbox
                                                                        value={selectedEntryIdSet.has(normalizeBillingEntryId(entry.id))}
                                                                        disabled={invoiceEditDisabled}
                                                                        ariaLabel={`Include expense ${entry.description || entry.id} on invoice`}
                                                                        onChange={(event) => toggleEntrySelection(entry.id, expenseEntryIds, event)}
                                                                    />
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                            {visibleExpenseEntries.length < filteredExpenseEntries.length && (
                                                <button type='button' className='invoice-load-more' disabled={editorBusy} onClick={() => setExpenseEntryLimit(limit => limit + ENTRY_PAGE_SIZE)}>
                                                    Load {Math.min(ENTRY_PAGE_SIZE, filteredExpenseEntries.length - visibleExpenseEntries.length).toLocaleString()} more billing expenses
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </section>
                {/* Flat Fee */}
                <section className='time-entries-section' aria-labelledby='invoice-flat-fee-entries-heading'>
                    <div className='invoice-entry-section-header'>
                        <h3 id='invoice-flat-fee-entries-heading'>
                            <button
                                type='button'
                                className='invoice-section-toggle'
                                aria-expanded={expandedFlat}
                                aria-controls='invoice-flat-fee-entries-content'
                                disabled={editorBusy}
                                onClick={() => setExpandedFlat(prev => !prev)}
                            >
                                <span className='invoice-section-toggle-copy'>
                                    <span className='invoice-section-title'>Flat Fee</span>
                                    <span className='invoice-section-summary'>
                                        <span className='invoice-section-summary-item'>{selectedFlatFeeEntries.length.toLocaleString()} selected</span>
                                        {flatFeeSelectionDateRange && (
                                            <span className='invoice-section-summary-item invoice-section-summary-detail'>
                                                <span className='invoice-section-summary-separator' aria-hidden='true'>•</span>
                                                <span>{flatFeeSelectionDateRange}</span>
                                            </span>
                                        )}
                                        <span className='invoice-section-summary-item invoice-section-summary-detail'>
                                            <span className='invoice-section-summary-separator' aria-hidden='true'>•</span>
                                            <span>{filteredFlatFeeEntries.length.toLocaleString()}{hasActiveEntryFilters ? ` of ${availableFlatFeeEntries.length.toLocaleString()}` : ''} available</span>
                                        </span>
                                    </span>
                                </span>
                                <ChevronDown className={expandedFlat ? 'expanded' : ''} size={16} aria-hidden='true' />
                            </button>
                        </h3>
                        <button
                            type='button'
                            className='add-time-entry'
                            title='Add flat fee entry'
                            aria-label='Add flat fee entry'
                            disabled={invoiceEditDisabled}
                            onClick={() => openNewBillingEntry('flat_fee')}
                        >
                            <Plus size={15} aria-hidden='true' />
                        </button>
                    </div>
                    {expandedFlat && (
                        <div id='invoice-flat-fee-entries-content'>
                            {filteredFlatFeeEntries.length === 0 ? (
                                <div className='empty-state'>{hasActiveEntryFilters ? 'No flat fee entries match the current filters.' : 'No flat fee entries for this invoice.'}</div>
                            ) : (
                                <>
                                    <div className='invoice-entry-table-scroll' role='region' aria-label='Flat fee entries' tabIndex={0}>
                                        <table className='payment-details-table table-full default invoice-entry-table invoice-flat-fee-entry-table'>
                                            <thead>
                                                <tr className='default'>
                                                    <th scope='col'>Date</th>
                                                    <th scope='col' className='invoice-entry-description'>Description</th>
                                                    <th scope='col'>Flat Fee Code</th>
                                                    <th scope='col'>Secondary Code</th>
                                                    <th scope='col'>Quantity</th>
                                                    <th scope='col'>Unit Price</th>
                                                    <th scope='col'>Unit</th>
                                                    <th scope='col'>Total</th>
                                                    <th scope='col'>Select</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {visibleFlatFeeEntries.map((entry) => {
                                                    const canEditEntry = !invoiceReadOnly
                                                        && billingEntryCanBeEditedFromInvoice(entry);
                                                    return (
                                                    <tr
                                                        key={entry.id}
                                                        className={`default ${canEditEntry ? 'invoice-entry-editable' : 'invoice-entry-snapshot'}`}
                                                        onClick={() => {
                                                            if (!invoiceEditDisabled && canEditEntry) editFlatFeeEntry(entry);
                                                        }}
                                                        title={canEditEntry
                                                            ? 'Click to edit flat fee entry'
                                                            : invoiceReadOnly
                                                                ? 'Remove the invoice payment before editing entries'
                                                                : 'Remove this entry from the invoice before editing it'}
                                                    >
                                                        <td>{formatBillingEntryDate(entry.date)}</td>
                                                        <td className='invoice-entry-description'>{entry.description}</td>
                                                        <td>{entry.flat_fee_code}</td>
                                                        <td>{entry.flat_fee_code_2 || entry.flat_fee_code2}</td>
                                                        <td>{toFiniteNumber(entry.final_quantity ?? entry.raw_quantity).toFixed(2)}</td>
                                                        <td>{formatValue(toFiniteNumber(entry.rate), 'currency')}</td>
                                                        <td>{entry.unit}</td>
                                                        <td>{formatValue(getBillingEntryTotal(entry), 'currency')}</td>
                                                        <td className='invoice-entry-select-cell' onClick={(event) => event.stopPropagation()}>
                                                            <Checkbox
                                                                value={selectedEntryIdSet.has(normalizeBillingEntryId(entry.id))}
                                                                disabled={invoiceEditDisabled}
                                                                ariaLabel={`Include flat fee ${entry.description || entry.id} on invoice`}
                                                                onChange={(event) => toggleEntrySelection(entry.id, flatFeeEntryIds, event)}
                                                            />
                                                        </td>
                                                    </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                    {visibleFlatFeeEntries.length < filteredFlatFeeEntries.length && (
                                        <button type='button' className='invoice-load-more' disabled={editorBusy} onClick={() => setFlatFeeEntryLimit(limit => limit + ENTRY_PAGE_SIZE)}>
                                            Load {Math.min(ENTRY_PAGE_SIZE, filteredFlatFeeEntries.length - visibleFlatFeeEntries.length).toLocaleString()} more flat fee entries
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </section>
            </div>
            {showCreateModal && (
                <Modal
                    onClose={closeCreateEntryModal}
                    title={createEntryData?.id
                        ? (createKind === 'time' ? 'Edit Time Entry' : createKind === 'expense' ? 'Edit Expense' : 'Edit Flat Fee')
                        : (createKind === 'time' ? 'Add Time Entry' : createKind === 'expense' ? 'Add Custom Expense' : 'Add Flat Fee')}
                    single
                    wide={createEntryData?.id ? true : false}
                    footer={(
                        <div className='modal-footer-actions'>
                            <button className='action alt' disabled={isSavingEntry || editorBusy} onClick={closeCreateEntryModal}>Cancel</button>
                            <button
                                className='action'
                                disabled={isSavingEntry || invoiceEditDisabled}
                                aria-busy={isSavingEntry}
                                onClick={saveBillingEntry}
                            >
                                {isSavingEntry ? 'Saving…' : 'Save'}
                            </button>
                        </div>
                    )}
                >
                    {createKind === 'time'
                        ? <CreateEntry users={users} entryData={createEntryData} setEntryData={setCreateEntryData} rateEntries={rateEntries} />
                        : <CreateFlatFee users={users} entryData={createEntryData} setEntryData={setCreateEntryData} flatFeeOptions={flatFeeOptions} flatFeeOptions2={flatFeeOptions2} entryKind={createKind} />
                    }
                </Modal>
            )}
            {showInvoiceFilePicker && (
                <MoveFolderPicker
                    isOpen={showInvoiceFilePicker}
                    onClose={() => setShowInvoiceFilePicker(false)}
                    onSelect={(path, file) => setLinkedInvoiceFile(file)}
                    fetchFolderContents={fetchInvoiceFolderContents}
                    caseName={caseData?.case?.case_name || 'Case Files'}
                    currentFolderPath='{{Name}}'
                    initialPath='{{Name}}'
                    initialSelection=''
                    selectMode='file'
                    title='Link invoice file'
                    instructions='Search or browse case files, then select the invoice document to link.'
                    confirmLabel='Link File'
                />
            )}
        </Modal>
        {entryMessage && (
            <Alert
                message={entryMessage.message}
                type={entryMessage.type}
                onClose={() => setEntryMessage(null)}
            />
        )}
        </>
    );
};
